// packages/shared/src/types/api-response.ts
import { z } from 'zod';

/**
 * Pagination metadata
 */
export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

/**
 * Response metadata
 */
export interface ResponseMeta {
  timestamp: string;
  requestId?: string;
  pagination?: PaginationMeta;
}

/**
 * Standard API success response envelope
 */
export interface ApiSuccessResponse<T> {
  success: true;
  data: T;
  message?: string;
  meta?: ResponseMeta;
}

/**
 * Standard API error detail
 */
export interface ApiErrorDetail {
  code: string;
  message: string;
  details?: Record<string, unknown> | Array<{ field: string; message: string }>;
  timestamp: string;
  path?: string;
}

/**
 * Standard API error response envelope
 */
export interface ApiErrorResponse {
  success: false;
  error: ApiErrorDetail;
  statusCode: number;
}

/**
 * Union type for API responses
 */
export type ApiResponse<T> = ApiSuccessResponse<T> | ApiErrorResponse;

/**
 * Paginated API response
 */
export type PaginatedApiResponse<T> = ApiSuccessResponse<T[]> & {
  meta: ResponseMeta & { pagination: PaginationMeta };
};

/**
 * Message-only response (for actions like logout, delete)
 */
export type MessageOnlyResponse = ApiSuccessResponse<null> & { message: string };

/**
 * Type guard for error responses
 */
export function isApiError(response: unknown): response is ApiErrorResponse {
  return (
    typeof response === 'object' &&
    response !== null &&
    'success' in response &&
    (response as Record<string, unknown>).success === false
  );
}

/**
 * Type guard for success responses
 */
export function isApiSuccess<T>(response: unknown): response is ApiSuccessResponse<T> {
  return (
    typeof response === 'object' &&
    response !== null &&
    'success' in response &&
    (response as Record<string, unknown>).success === true
  );
}

/**
 * Unwrap data from API response (throws if error)
 */
export function unwrapResponse<T>(response: ApiResponse<T>): T {
  if (isApiError(response)) {
    throw new Error(response.error.message);
  }
  return response.data;
}

// Zod Schemas for validation

export const PaginationMetaSchema = z.object({
  page: z.number(),
  limit: z.number(),
  total: z.number(),
  totalPages: z.number(),
  hasNextPage: z.boolean(),
  hasPreviousPage: z.boolean(),
});

export const ResponseMetaSchema = z.object({
  timestamp: z.string(),
  requestId: z.string().optional(),
  pagination: PaginationMetaSchema.optional(),
});

export const ApiErrorDetailSchema = z.object({
  code: z.string(),
  message: z.string(),
  details: z
    .union([z.record(z.unknown()), z.array(z.object({ field: z.string(), message: z.string() }))])
    .optional(),
  timestamp: z.string(),
  path: z.string().optional(),
});

export const ApiErrorResponseSchema = z.object({
  success: z.literal(false),
  error: ApiErrorDetailSchema,
  statusCode: z.number(),
});

/**
 * Create a success response schema for a given data schema
 */
export const createApiSuccessSchema = <T extends z.ZodType>(dataSchema: T) =>
  z.object({
    success: z.literal(true),
    data: dataSchema,
    message: z.string().optional(),
    meta: ResponseMetaSchema.optional(),
  });

/**
 * Create a paginated response schema for a given item schema
 */
export const createPaginatedSchema = <T extends z.ZodType>(itemSchema: T) =>
  z.object({
    success: z.literal(true),
    data: z.array(itemSchema),
    meta: z.object({
      timestamp: z.string(),
      requestId: z.string().optional(),
      pagination: PaginationMetaSchema,
    }),
  });

/**
 * Create a flat paginated schema matching the POST-INTERCEPTOR shape returned
 * by the Axios client after it strips the backend envelope.
 *
 * The Axios response interceptor in apps/web converts:
 *   { success: true, data: T[], meta: { pagination: { page, limit, total, totalPages, ... } } }
 * into:
 *   { data: T[], total: number, page: number, limit: number, totalPages: number }
 *
 * Use this schema in endpoint-registry.ts for paginated GET endpoints so that
 * the response schema matches what calling code actually receives.
 */
export const createFlatPaginatedSchema = <T extends z.ZodType>(itemSchema: T) =>
  z.object({
    data: z.array(itemSchema),
    total: z.number(),
    page: z.number(),
    limit: z.number(),
    totalPages: z.number(),
  });
